import * as DialogPrimitive from "@radix-ui/react-dialog";
import { XIcon } from "lucide-react";
import * as React from "react";

import { cn } from "@/shadcn/lib/utils";

import { Button } from "../Button/Button";

export type DialogProps = {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly title?: React.ReactNode;
  readonly description?: React.ReactNode;
  readonly children?: React.ReactNode;
  readonly contentClassName?: string;
  readonly ariaLabel?: string;
  /**
   * Where the surface sits. Centered, it moves by half of every change in its height, which
   * drags a popover anchored inside it (a date picker's time columns) out from under the
   * pointer. "top" pins it to the top of the window, so content that grows only extends down.
   */
  readonly align?: "center" | "top";
  /**
   * Theme for the dialog surface. The dialog portals to `body`, outside any page-level `data-theme`
   * scope, so a brand whose tokens (`--primary`, fonts) are scoped that way must be re-declared here or
   * the surface falls back to the RepeatMD palette.
   */
  readonly theme?: "ageless" | "repeatmd";
  /** When false, children render directly — use when the dialog manages its own scroll layout. */
  readonly manageContentScroll?: boolean;
  /** Restyles the built-in close button — use when it sits on a custom surface (e.g. an image or gradient header). */
  readonly closeClassName?: string;
  /** Accessible name of the built-in close button, e.g. "Finish later". */
  readonly closeLabel?: string;
  /** When false, Escape and backdrop clicks don't close the dialog; use for long forms. */
  readonly lightDismiss?: boolean;
  /**
   * Optional ref to the dialog surface element.
   */
  readonly contentRef?: React.RefCallback<HTMLDivElement>;
  /**
   * When true, opening focuses the dialog SURFACE instead of the first control inside it.
   *
   * Focus still enters the dialog — assistive tech announces it via `aria-label`/title, and
   * the rest of the page is inert — but no visible focus ring lands on the close button, so
   * a mouse user does not see a stray highlight. Tab moves into the controls as usual.
   *
   * Do NOT change this to skip focus entirely: leaving focus on the trigger while the page
   * behind is pointer-disabled strands screen-reader users outside a modal they cannot see
   * announced.
   */
  readonly disableOpenAutoFocus?: boolean;
  /**
   * Runs when the dialog closes and Radix is about to move focus. Call `event.preventDefault()` and focus the
   * element that opened the dialog when that element is outside this Root (a trigger that only toggles `open`),
   * since Radix otherwise lands focus on the document body.
   */
  readonly onCloseAutoFocus?: (event: Event) => void;
};

export const Dialog = ({
  open,
  onClose,
  title,
  description,
  children,
  contentClassName,
  ariaLabel,
  align = "center",
  manageContentScroll = true,
  closeClassName,
  closeLabel,
  lightDismiss = true,
  disableOpenAutoFocus = false,
  onCloseAutoFocus,
  contentRef,
  theme,
}: DialogProps) => {
  // Focus target for `disableOpenAutoFocus` — see the prop's doc comment.
  const surfaceRef = React.useRef<HTMLDivElement>(null);
  const handleContentRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      surfaceRef.current = node;
      contentRef?.(node);
    },
    [contentRef],
  );

  const surface = (
    <>
      {(title || description) && (
        <DialogHeader>
          {title && (
            <DialogPrimitive.Title>
              {typeof title === "string" ? (
                <span className="block type-heading-sm text-foreground">{title}</span>
              ) : (
                title
              )}
            </DialogPrimitive.Title>
          )}
          {description && (
            <DialogPrimitive.Description>
              {typeof description === "string" ? (
                <span className="block type-body-sm text-foreground">{description}</span>
              ) : (
                description
              )}
            </DialogPrimitive.Description>
          )}
          <DialogClose
            className={cn("absolute top-4 right-4", closeClassName)}
            label={closeLabel}
          />
        </DialogHeader>
      )}

      {!title && !description && (
        <DialogClose className={cn("absolute top-4 right-4", closeClassName)} label={closeLabel} />
      )}
      {/* Radix announces the dialog by its Title; one named by `ariaLabel` alone keeps that
          name as a hidden title rather than going unannounced. */}
      {!title && ariaLabel && (
        <DialogPrimitive.Title className="sr-only">{ariaLabel}</DialogPrimitive.Title>
      )}

      {manageContentScroll ? (
        // -mx-1 px-1 moves the horizontal clip edge 4px out without moving the content, so the
        // 1px outside rings that form fields draw as borders are not cut off at the sides.
        <div className="-mx-1 min-h-0 flex-1 scrollbar-none overflow-x-hidden overflow-y-auto px-1 [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
          {children}
        </div>
      ) : (
        children
      )}
    </>
  );

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            "fixed inset-0 z-120 bg-neutral-800/50",
            "data-[state=closed]:animate-dialogOverlayOut data-[state=open]:animate-dialogOverlayIn",
          )}
        />

        <DialogPrimitive.Content
          aria-describedby={undefined}
          aria-label={ariaLabel}
          onCloseAutoFocus={onCloseAutoFocus}
          onEscapeKeyDown={(event) => {
            // A control inside with its own popup open (a date picker, a combobox) owns this
            // Escape: the popup closes on the same keydown, and the dialog closing with it would
            // drop the user a layer further out than they asked. Checked on the surface, not the
            // key's target: clicking into the popup can leave focus on the surface itself.
            if (
              !lightDismiss ||
              surfaceRef.current?.querySelector('[aria-haspopup][aria-expanded="true"]') != null
            ) {
              event.preventDefault();
            }
          }}
          onInteractOutside={lightDismiss ? undefined : (event) => event.preventDefault()}
          onOpenAutoFocus={
            disableOpenAutoFocus
              ? (event) => {
                  // Take focus off Radix's default target (the first control) and put it on
                  // the surface, which is `tabIndex={-1}` and has no focus outline.
                  event.preventDefault();
                  surfaceRef.current?.focus();
                }
              : undefined
          }
          className={cn(
            // Center via flex layout, not a `-50%` transform: the transform is recomputed against
            // the box's own size, so it drifts/snaps when content reflows after open (step wizards,
            // live previews, async images). pointer-events-none lets backdrop clicks fall through to
            // the Overlay to dismiss; the surface re-enables them. Mirrors Modal.tsx.
            "pointer-events-none fixed inset-0 z-121 flex justify-center focus:outline-hidden",
            align === "top" ? "items-start pt-16" : "items-center",
            "[&[data-state=closed]_[data-modal-surface]]:animate-dialogSurfaceOut",
            "[&[data-state=open]_[data-modal-surface]]:animate-dialogSurfaceIn",
          )}
        >
          <div
            className={cn(
              "pointer-events-auto relative flex max-h-[85vh] w-[calc(100%-2rem)] max-w-[560px] flex-col gap-4 overflow-hidden rounded-2xl border border-border bg-white p-6 shadow-xl",
              // No ring when focus is redirected here on open; real keyboard navigation
              // still rings the controls inside.
              "focus:outline-hidden",
              contentClassName,
            )}
            data-modal-surface
            data-theme={theme}
            ref={handleContentRef}
            tabIndex={-1}
          >
            {surface}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};

export type DialogHeaderProps = React.HTMLAttributes<HTMLDivElement>;
export const DialogHeader = React.forwardRef<HTMLDivElement, DialogHeaderProps>(
  ({ className, ...props }, ref) => (
    <div className={cn("grid gap-1 pr-10", className)} ref={ref} {...props} />
  ),
);
DialogHeader.displayName = "DialogHeader";

export type DialogBodyProps = React.HTMLAttributes<HTMLDivElement>;
export const DialogBody = React.forwardRef<HTMLDivElement, DialogBodyProps>(
  ({ className, ...props }, ref) => (
    <div className={cn("grid gap-3", className)} ref={ref} {...props} />
  ),
);
DialogBody.displayName = "DialogBody";

export type DialogFooterProps = React.HTMLAttributes<HTMLDivElement>;
export const DialogFooter = React.forwardRef<HTMLDivElement, DialogFooterProps>(
  ({ className, ...props }, ref) => (
    <div
      className={cn("flex items-center justify-end gap-3 pt-2", className)}
      ref={ref}
      {...props}
    />
  ),
);
DialogFooter.displayName = "DialogFooter";

export type DialogCloseProps = Omit<React.ComponentPropsWithoutRef<"button">, "type"> & {
  readonly label?: string;
};

export const DialogClose = React.forwardRef<HTMLButtonElement, DialogCloseProps>(
  ({ className, label = "Close dialog", onClick, ...props }, ref) => {
    return (
      <DialogPrimitive.Close asChild>
        <Button
          aria-label={label}
          className={cn("h-9 w-9 p-0", className)}
          ref={ref}
          size="icon"
          type="button"
          variant="ghost"
          {...props}
          onClick={(e) => {
            onClick?.(e);
          }}
        >
          <span aria-hidden="true">
            <XIcon className="h-4 w-4" />
          </span>
        </Button>
      </DialogPrimitive.Close>
    );
  },
);

DialogClose.displayName = "DialogClose";
